/**
 * PortfoliYo Lab — the login screen (PB-4).
 *
 * Lab's own, on Lab's origin. Before this, an unauthenticated visitor to lab.<domain> got the
 * research shell with every API call failing 401 behind it, and a "Sign in" link to Trade.
 *
 * Two steps in one component, not two routes. The email is needed to verify the code, and holding
 * it in a parent route's state or a query string would either lose it on refresh or put an address
 * in browser history and server logs for no gain.
 */
import { useState, type FormEvent } from 'react';
import { FlaskConical } from 'lucide-react';
import { useLabAuth } from './LabAuthContext';
import { LabAuthError } from './labAuthApi';

/**
 * Trade's origin, for the product-switch link only — never for authentication.
 *
 * This is the whole remaining role of `VITE_TRADE_ORIGIN`: a link, the mirror of Trade's "Open
 * PortfoliYo Lab" (PB-1). Unset renders nothing rather than a dead link, which is right for a
 * deployment that runs no Trade service.
 */
const TRADE_ORIGIN = (import.meta.env.VITE_TRADE_ORIGIN as string | undefined) ?? '';

export function LabLogin() {
  const { requestCode, verifyCode } = useLabAuth();
  const [step, setStep] = useState<'email' | 'code'>('email');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (event: FormEvent, action: () => Promise<void>) => {
    event.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await action();
    } catch (caught) {
      // `LabAuthError.message` is the server's `detail`, which is deliberately vague about *why* a
      // code was rejected — see app/auth/routes.py. Rendered as-is rather than reinterpreted here,
      // so the frontend cannot accidentally become more informative than the API meant to be.
      setError(caught instanceof LabAuthError ? caught.message : 'Something went wrong. Try again.');
    } finally {
      setBusy(false);
    }
  };

  const sendCode = (event: FormEvent) =>
    submit(event, async () => {
      await requestCode(email);
      setStep('code');
    });

  const signIn = (event: FormEvent) => submit(event, () => verifyCode(email, code));

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50 px-4">
      <div className="w-full max-w-sm">
        <div className="mb-6 flex items-center justify-center gap-2">
          <FlaskConical className="h-7 w-7 text-emerald-700" aria-hidden="true" />
          <h1 className="text-2xl font-semibold text-slate-900">PortfoliYo Lab</h1>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
          {step === 'email' ? (
            <form onSubmit={sendCode} className="space-y-4">
              <div>
                <label htmlFor="lab-email" className="block text-sm font-medium text-slate-700">
                  Email
                </label>
                <input
                  id="lab-email"
                  type="email"
                  required
                  autoFocus
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-emerald-600 focus:outline-none focus:ring-1 focus:ring-emerald-600"
                />
              </div>
              <button
                type="submit"
                disabled={busy}
                className="w-full rounded-lg bg-emerald-700 px-4 py-2.5 text-sm font-semibold text-white hover:bg-emerald-800 disabled:opacity-60"
              >
                {busy ? 'Sending…' : 'Send login code'}
              </button>
            </form>
          ) : (
            <form onSubmit={signIn} className="space-y-4">
              <div>
                <label htmlFor="lab-code" className="block text-sm font-medium text-slate-700">
                  Enter verification code
                </label>
                <p className="mt-1 text-xs text-slate-500">Sent to {email}</p>
                <input
                  id="lab-code"
                  // `inputMode="numeric"` and `one-time-code` are what make a phone show a number
                  // pad and offer the code from the mail app — the difference between typing six
                  // digits and copying them between apps.
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  pattern="[0-9]{6}"
                  maxLength={6}
                  required
                  autoFocus
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                  className="mt-2 w-full rounded-lg border border-slate-300 px-3 py-2 text-center text-lg tracking-[0.4em] focus:border-emerald-600 focus:outline-none focus:ring-1 focus:ring-emerald-600"
                />
              </div>
              <button
                type="submit"
                disabled={busy}
                className="w-full rounded-lg bg-emerald-700 px-4 py-2.5 text-sm font-semibold text-white hover:bg-emerald-800 disabled:opacity-60"
              >
                {busy ? 'Signing in…' : 'Sign in'}
              </button>
              <button
                type="button"
                onClick={() => {
                  setStep('email');
                  setCode('');
                  setError(null);
                }}
                className="w-full text-xs text-slate-500 hover:text-slate-800"
              >
                Use a different email
              </button>
            </form>
          )}

          {error && (
            <p role="alert" className="mt-4 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">
              {error}
            </p>
          )}
        </div>

        {TRADE_ORIGIN && (
          <p className="mt-6 text-center text-sm">
            <a
              href={TRADE_ORIGIN}
              className="text-slate-500 underline-offset-2 hover:text-slate-900 hover:underline"
            >
              Open PortfoliYo Trade
            </a>
          </p>
        )}
      </div>
    </div>
  );
}
